import type { WmsTemplateCode } from '@/lib/wms-template-config';
import type {
  NavigationCategory,
  NavigationPolicyConfig,
  SurfaceNavigationConfig,
} from '@/lib/use-layout-config';
import type {
  WarehouseModernizationStage,
  WarehouseNavigationMode,
  WarehouseNavigationPolicyConfig,
} from '@/lib/warehouse-navigation-governance';

export const AUTO_TEMPLATE_VALUE = '__auto__' as const;

export interface MenuConfigCategoryRow {
  id: string;
  visible: boolean;
  icon: string;
  requiredPermission: string;
  labelOverride?: string;
  order: number;
}

export interface PersistedNavConfigPayload {
  navMode: 'vertical' | 'horizontal';
  mobileNavStyle: 'tabs' | 'drawer';
  categoryOrder: string[];
  categoryIcons?: Record<string, string>;
  navigationPolicy?: NavigationPolicyConfig;
  warehouseProfileTemplateCode?: WmsTemplateCode;
  warehouseNavigationPolicy?: WarehouseNavigationPolicyConfig;
  navigation?: MenuConfigCategoryRow[] | NavigationCategory[];
  pwaNavigation?: SurfaceNavigationConfig;
  extensionNavigation?: SurfaceNavigationConfig;
  [key: string]: unknown;
}

export interface BuildTenantNavConfigPayloadParams {
  loadedConfigValue: Record<string, unknown>;
  rows: MenuConfigCategoryRow[];
  navMode: 'vertical' | 'horizontal';
  mobileNavStyle: 'tabs' | 'drawer';
  savedNavigationPolicy?: NavigationPolicyConfig;
  pwaNavigation?: SurfaceNavigationConfig;
  extensionNavigation?: SurfaceNavigationConfig;
  warehouseTemplateCode: typeof AUTO_TEMPLATE_VALUE | WmsTemplateCode;
  warehouseNavMode: WarehouseNavigationMode;
  warehouseModernizationStage: WarehouseModernizationStage;
  preserveLegacyWarehouseModules: boolean;
}

function isNavigationCategory(value: unknown): value is NavigationCategory {
  return Boolean(value)
    && typeof value === 'object'
    && typeof (value as { id?: unknown }).id === 'string'
    && Array.isArray((value as { items?: unknown }).items);
}

function isNavigationCategoryArray(value: unknown): value is NavigationCategory[] {
  return Array.isArray(value) && value.every(isNavigationCategory);
}

export function buildTenantNavConfigPayload(
  params: BuildTenantNavConfigPayloadParams,
): PersistedNavConfigPayload {
  const nextNavigationPolicy: NavigationPolicyConfig = {
    ...(params.savedNavigationPolicy ?? {}),
    hiddenCategoryIds: params.rows.filter((row) => !row.visible).map((row) => row.id),
  };
  const nextWarehouseNavigationPolicy: WarehouseNavigationPolicyConfig = {
    mode: params.warehouseNavMode,
    wmModernizationStage: params.warehouseModernizationStage,
    preserveLegacyModulesUntilReady: params.preserveLegacyWarehouseModules,
  };

  const {
    navigation: existingNavigation,
    pwaNavigation: _existingPwa,
    extensionNavigation: _existingExtension,
    navigationPolicy: _existingNavigationPolicy,
    warehouseProfileTemplateCode: _existingWarehouseTemplateCode,
    warehouseNavigationPolicy: _existingWarehouseNavigationPolicy,
    categoryIcons: _existingCategoryIcons,
    categoryOrder: _existingCategoryOrder,
    navMode: _existingNavMode,
    mobileNavStyle: _existingMobileNavStyle,
    ...restConfig
  } = params.loadedConfigValue;

  return {
    ...restConfig,
    navMode: params.navMode,
    mobileNavStyle: params.mobileNavStyle,
    categoryOrder: params.rows.map((row) => row.id),
    categoryIcons: Object.fromEntries(params.rows.map((row) => [row.id, row.icon])),
    navigationPolicy: nextNavigationPolicy,
    warehouseProfileTemplateCode: params.warehouseTemplateCode === AUTO_TEMPLATE_VALUE
      ? undefined
      : params.warehouseTemplateCode,
    warehouseNavigationPolicy: nextWarehouseNavigationPolicy,
    ...(isNavigationCategoryArray(existingNavigation)
      ? { navigation: existingNavigation }
      : {}),
    ...(params.pwaNavigation ? { pwaNavigation: params.pwaNavigation } : {}),
    ...(params.extensionNavigation ? { extensionNavigation: params.extensionNavigation } : {}),
  };
}
